import * as React from 'react'
import cn from 'clsx'
import { useTranslation } from 'react-i18next'
import { NavLink } from '@remix-run/react'
import { Container, Heading, Text, Tooltip } from '@radix-ui/themes'

import { Payment } from '~/components/common/payment'
import { Copyright, I18n, SmartMedia, SocialMedia } from '~/routes/_layout'
import { ROOT_I18NEXT } from '~/routes/_layout/root.component'
import type { App } from '~/routes/_layout/root.component'
import type { Locale } from '~/models/locale'


type FooterProps = {
  app: App
  locale: Locale
  setApp: React.Dispatch<React.SetStateAction<App>>
}

type LinkItemProps = {
  label: string
  locale: Locale
  to: string
  setApp: React.Dispatch<React.SetStateAction<App>>
}


const LinkItem: React.FC<LinkItemProps> = ({ label, locale, to, setApp }) => (
  <li>
    <NavLink
      end={to === ''}
      to={`/${locale}${to}`}
      prefetch="viewport"
      className={({ isActive }) => cn(
        'p-1.5 -ml-1.5',
        'transition duration-200 ease-in-out',
        'hover:font-medium hover:text-accent-8', {
          'text-gray-9': !isActive,
          'font-bold text-accent-8': isActive,
        })}
      onClick={() => setApp((prev) => ({
        ...prev,
        options: {
          ...prev.options,
          redirect: null,
        },
      }))}
    >
      {label}
    </NavLink>
  </li>
)

const Footer: React.FC<FooterProps> = ({ locale, setApp }) => {
  const { t } = useTranslation(ROOT_I18NEXT.routeRoot)

  return (
    <footer id="footer" className="pt-6 border-t border-gray-6 bg-gray-5">
      <Container px="5">
        <nav className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-10 py-8 gap-8">
          <div className="flex flex-col items-center md:items-start md:col-span-3">
            <Heading as="h4" weight="medium" mb="1" size="3">
              {t('label.general')}
            </Heading>
            <ul className="flex flex-col items-center md:items-start gap-y-1 text-gray-10 tracking-tight">
              {[
                { label: t('label.home'), to: '' },
                { label: t('label.products'), to: '/products' },
                { label: t('label.profile'), to: '/profile' },
                { label: t('label.contact'), to: '/contact' },
                { label: t('label.certificates'), to: '/contact#certificates' },
              ].map((props) => (
                <LinkItem key={props.label} locale={locale} setApp={setApp} {...props} />
              ))}
            </ul>
          </div>

          <div className="flex flex-col items-center md:items-start md:col-span-3">
            <Heading as="h4" weight="medium" mb="1" size="3">
              {t('label.legal')}
            </Heading>
            <ul className="flex flex-col items-center md:items-start gap-y-1 text-gray-10 tracking-tight">
              {[
                { label: t('label.refundPolicy'), to: '/legal/refund-policy' },
                { label: t('label.privacyPolicy'), to: '/legal/privacy-policy' },
                { label: t('label.termsOfService'), to: '/legal/terms-of-service' },
                { label: t('label.legalNotice'), to: '/legal/legal-notice' },
              ].map((props) => (
                <LinkItem key={props.label} locale={locale} setApp={setApp} {...props} />
              ))}
            </ul>
          </div>

          <div className="flex flex-col items-center md:items-start md:col-span-2">
            <Heading as="h4" weight="medium" mb="2" size="3">
              {t('label.socialMedia')}
            </Heading>
            <SocialMedia />
          </div>

          <div className="mt-5 md:mt-0 justify-self-center md:justify-self-end sm:col-span-2">
            <Tooltip content={t('label.changeLanguage')} side="bottom" sideOffset={10}>
              <Text>
                <I18n locale={locale} />
              </Text>
            </Tooltip>
          </div>

          <div className="flex flex-col items-center md:items-start sm:col-span-2 md:col-span-5">
            <Heading as="h4" weight="medium" mb="2" size="3">
              {t('label.paymentMethods')}
            </Heading>
            <Payment className="flex items-center justify-center sm:justify-start gap-x-2 flex-wrap" />
          </div>
        </nav>
        <div className="flex flex-col sm:flex-row justify-between gap-1 py-5 items-center sm:items-end">
          <Copyright />
          <SmartMedia />
        </div>
      </Container>
    </footer>
  )
}
Footer.displayName = 'Footer'


export { Footer }
